import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Search, TrendingUp, Users, DollarSign, Target } from 'lucide-react';
import { salesService } from '@/services/salesService';
import { Lead, KanbanData, EtapaLead, ETAPA_LABELS, ETAPA_COLORS } from '@/types/sales';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import KanbanColumn from '@/components/sales/KanbanColumn';
import LeadCard from '@/components/sales/LeadCard';
import LeadModal from '@/components/sales/LeadModal';

const Sales = () => {
  const { user } = useAuth();
  const [kanbanData, setKanbanData] = useState<KanbanData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [activeLead, setActiveLead] = useState<Lead | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Carregar kanban
  const loadKanban = async () => {
    try {
      setIsLoading(true);
      const data = await salesService.getKanban();
      setKanbanData(data);
    } catch (error) {
      console.error('Erro ao carregar kanban:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar leads',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadKanban();
  }, []);

  // Estatísticas
  const totalLeads = Object.values(kanbanData).reduce((acc, leads) => acc + leads.length, 0);
  const leadsAtivos = (kanbanData[EtapaLead.EM_CONTATO]?.length || 0) + 
                      (kanbanData[EtapaLead.APRESENTACAO_AGENDADA]?.length || 0) +
                      (kanbanData[EtapaLead.PROPOSTA_ENVIADA]?.length || 0);
  const propostasEnviadas = kanbanData[EtapaLead.PROPOSTA_ENVIADA]?.length || 0;
  const propostasAceitas = kanbanData[EtapaLead.PROPOSTA_ACEITA]?.length || 0;

  // Handlers DnD
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const leadId = active.id as number;
    
    // Encontrar o lead
    for (const etapa of Object.keys(kanbanData)) {
      const lead = kanbanData[etapa].find(l => l.id === leadId);
      if (lead) {
        setActiveLead(lead);
        break;
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveLead(null);
    
    if (!over) return;
    
    const leadId = active.id as number;
    const novaEtapa = over.id as EtapaLead;
    
    // Encontrar etapa atual do lead
    let lead: Lead | null = null;
    let etapaAtual: EtapaLead | null = null;
    
    for (const etapa of Object.keys(kanbanData)) {
      const foundLead = kanbanData[etapa].find(l => l.id === leadId);
      if (foundLead) {
        lead = foundLead;
        etapaAtual = etapa as EtapaLead;
        break;
      }
    }
    
    if (!lead || !etapaAtual || etapaAtual === novaEtapa) return;
    
    try {
      // Atualizar no backend
      await salesService.moverLeadEtapa(leadId, novaEtapa);
      
      // Atualizar estado local
      setKanbanData(prev => {
        const newData = { ...prev };
        newData[etapaAtual!] = newData[etapaAtual!].filter(l => l.id !== leadId);
        newData[novaEtapa] = [...(newData[novaEtapa] || []), { ...lead!, etapa_atual: novaEtapa }];
        return newData;
      });
      
      toast({
        title: 'Lead movido',
        description: `${lead.nome_empresa} movido para ${ETAPA_LABELS[novaEtapa]}`
      });
      
    } catch (error: any) {
      console.error('Erro ao mover lead:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Não foi possível mover o lead',
        variant: 'destructive'
      });
    }
  };

  const handleLeadClick = (lead: Lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  const handleCreateLead = () => {
    setSelectedLead(null);
    setShowLeadModal(true);
  };

  const handleLeadSaved = () => {
    loadKanban();
    setShowLeadModal(false);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-3xl font-bold tracking-tight">CRM Comercial</h2>
            <p className="text-muted-foreground">
              Gerencie seus leads e acompanhe o pipeline de vendas
            </p>
          </div>
          
          <Button onClick={handleCreateLead} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="mr-2 h-4 w-4" />
            Novo Lead
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Leads</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalLeads}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Leads Ativos</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{leadsAtivos}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Propostas Enviadas</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{propostasEnviadas}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Propostas Aceitas</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{propostasAceitas}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Kanban Board */}
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-4">
            {Object.values(EtapaLead).map(etapa => (
              <KanbanColumn
                key={etapa}
                etapa={etapa}
                leads={kanbanData[etapa] || []}
                onLeadClick={handleLeadClick}
                searchTerm={searchTerm}
              />
            ))}
          </div>

          <DragOverlay>
            {activeLead ? (
              <div className="rotate-2 opacity-90">
                <LeadCard lead={activeLead} onClick={() => {}} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Lead Modal */}
        <LeadModal
          isOpen={showLeadModal}
          onClose={() => setShowLeadModal(false)}
          lead={selectedLead}
          onSave={handleLeadSaved}
        />
      </div>
    </Layout>
  );
};

export default Sales;


